import { isNil } from 'lodash';
import moment from 'moment';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from '@shared/components/View';
import { Calendar } from '@shared/components/dateAndTime/calendar';
import {
  FocusRef,
  MBComboDatePickerField,
} from '@shared/components/dateAndTime/comboDatePicker/field';
import { MBTimePicker } from '@shared/components/dateAndTime/timePicker';
import { grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { DATE_FORMATS, FMT_LOCAL_DATE, FMT_TIME, TIME_FORMATS } from './constants';
import { Props } from './props';

/*
Note: defaultTime is used as a prefill value for the time field.
It's applied when the picker is opened, but can be cleared by the user.

This is not currently set up to *force* a default time to be applied.
*/

export function ComboDatePicker({
  mode,
  value,
  label = 'Due Date',
  defaultTime,
  onChange,
  autoFocus,
  highlightDates,
  onMonthChange,
  layoutStyle = {},
}: Props) {
  const isDesktop = useIsDesktop();
  const [currentPicker, setCurrentPicker] = useState<'date' | 'time'>('date');
  const dateFieldRef = useRef<FocusRef>(null);
  const handleFocusDateField = useCallback(() => setCurrentPicker('date'), []);
  const handleFocusTimeField = useCallback(() => setCurrentPicker('time'), []);

  const selectedDates = useMemo(() => (!!value?.start ? [value.start] : undefined), [value?.start]);

  const layout: CSSProperties = useMemo(() => {
    return isDesktop ? { width: grid(37) } : { flex: 1, alignSelf: 'stretch', ...layoutStyle };
  }, [isDesktop, layoutStyle]);

  const [defaultWasUsed, setDefaultWasUsed] = useState(false);

  useEffect(() => {
    if (isNil(value?.start) && !isNil(defaultTime) && !defaultWasUsed) {
      onChange({ start: defaultTime });
      setDefaultWasUsed(true);
    }
  }, [defaultTime, value?.start, onChange, defaultWasUsed]);

  const effectiveDefaultTime = defaultWasUsed ? undefined : defaultTime;
  const startValue = value?.start ?? effectiveDefaultTime;

  const handleChangeStartDate = useCallback(
    (dateString: string) => {
      if (isNil(dateString)) {
        onChange({ start: undefined });
        return;
      }

      const updated = moment(dateString);
      const existing = moment(value?.start ?? defaultTime);
      updated.hours(existing.hours());
      updated.minutes(existing.minutes());
      updated.seconds(existing.seconds());

      onChange({ start: updated.toISOString() });
    },
    [value?.start, defaultTime, onChange],
  );

  const handleChangeStartTime = useCallback(
    (timeString: string) => {
      if (isNil(timeString)) {
        onChange({ start: undefined });
        return;
      }

      const updated = moment(timeString);
      const existing = moment(value?.start ?? defaultTime);
      updated.year(existing.year());
      updated.month(existing.month());
      updated.date(existing.date());

      onChange({ start: updated.toISOString() });
    },
    [value?.start, defaultTime, onChange],
  );

  const handleTimePickerClose = useCallback(() => {
    setCurrentPicker('date');
    // After switching back to date picker, move focus to the date field so the calendar is highlighted
    dateFieldRef.current?.focus?.();
  }, []);

  return (
    <View style={{ padding: grid(1), ...layout }}>
      {mode.date === 'single' && (
        <View row>
          <MBComboDatePickerField
            label={label}
            icon="calendar"
            value={!isNil(startValue) ? moment(startValue).format(FMT_LOCAL_DATE) : ''}
            style={{ width: '60%' }}
            formats={DATE_FORMATS}
            onFocus={handleFocusDateField}
            onChange={handleChangeStartDate}
            forwardRef={dateFieldRef}
            autoFocus={autoFocus}
          />
          {!isNil(mode.time) && (
            <MBComboDatePickerField
              label={(mode.time === 'range' ? 'Start ' : '') + 'Time'}
              value={!isNil(startValue) ? moment(startValue).format(FMT_TIME) : ''}
              style={{ width: '40%' }}
              textAlign="right"
              formats={TIME_FORMATS}
              onFocus={handleFocusTimeField}
              onChange={handleChangeStartTime}
            />
          )}
        </View>
      )}
      {currentPicker === 'date' && (
        <Calendar
          onSelectDate={handleChangeStartDate}
          selectedDates={selectedDates}
          highlightDates={highlightDates}
          onMonthChange={onMonthChange}
        />
      )}
      {currentPicker === 'time' && (
        <MBTimePicker
          style={{ marginTop: grid(1), height: grid(40) }}
          value={startValue ?? ''}
          onChange={handleChangeStartTime}
          onClose={handleTimePickerClose}
          showClose
        />
      )}
    </View>
  );
}
